import React, { useEffect } from "react";
import { Check, ExternalLink, X } from "lucide-react";
import useClickOutsideModal from "../common/useClickOutsideModal";

const FIELD_LABELS = {
  name: "Full Name",
  fathername: "Father's Name",
  date_of_birth: "Date of Birth",
  cgpa: "CGPA",
  degree: "Degree",
  institution: "Institution",
};

const formatScore = (score) => {
  const numericScore = Number(score);
  if (Number.isNaN(numericScore)) {
    return "0%";
  }

  const percentage = numericScore <= 1 ? numericScore * 100 : numericScore;
  return `${Math.round(percentage)}%`;
};

const MatchBadge = ({ matched }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
      matched
        ? "bg-[#DCFCE7] text-[#166534]"
        : "bg-[#FEE2E2] text-[#991B1B]"
    }`}
  >
    {matched ? <Check size={12} /> : <X size={12} />}
    {matched ? "Matched" : "Not Matched"}
  </span>
);

const TranscriptAiReportModal = ({
  isOpen,
  onClose,
  instructorInformation,
}) => {
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const modalRef = useClickOutsideModal(onClose);

  if (!isOpen) {
    return null;
  }

  const transcriptVerification =
    instructorInformation?.transcriptVerification || {};
  const aiReport = transcriptVerification?.aiReport;
  const transcriptUrl = instructorInformation?.transcript?.secureUrl;
  const fieldEntries = aiReport?.fields
    ? Object.entries(aiReport.fields)
    : [];

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4 sm:px-4 sm:py-6">
      <div
        ref={modalRef}
        className="flex max-h-[min(94vh,calc(100dvh-2rem))] w-full max-w-[640px] flex-col overflow-hidden rounded-xl bg-white"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[#E5E7EB] p-4 sm:p-6">
          <h3 className="text-lg font-bold text-[#111827] sm:text-2xl">
            Transcript AI Verification Report
          </h3>
          <button
            type="button"
            aria-label="Close AI report"
            onClick={onClose}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-[#6B7280]"
          >
            <X size={20} strokeWidth={2} />
          </button>
        </div>

        <div className="overflow-y-auto p-4 sm:p-6">
          {aiReport ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-4">
                <div>
                  <p className="text-sm text-[#6B7280]">Overall Result</p>
                  <div className="mt-1">
                    <MatchBadge matched={Boolean(aiReport.match)} />
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#6B7280]">Match Score</p>
                  <p className="text-lg font-semibold text-[#111827]">
                    {formatScore(aiReport.score)}
                  </p>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                {fieldEntries.length > 0 ? (
                  fieldEntries.map(([key, field]) => (
                    <div
                      key={key}
                      className="rounded-lg border border-[#E5E7EB] p-3 sm:p-4"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-medium text-[#374151]">
                          {FIELD_LABELS[key] || key}
                        </p>
                        <MatchBadge matched={Boolean(field?.matched)} />
                      </div>
                      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                        <p className="text-[#6B7280]">
                          Extracted:{" "}
                          <span className="text-[#111827]">
                            {field?.extracted || "Not detected"}
                          </span>
                        </p>
                        <p className="text-[#6B7280]">
                          Score:{" "}
                          <span className="text-[#111827]">
                            {formatScore(field?.score)}
                          </span>
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-[#6B7280]">
                    No field-level comparison data available.
                  </p>
                )}
              </div>
            </>
          ) : (
            <p className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-4 text-sm text-[#6B7280]">
              No AI verification report is available for this transcript yet.
            </p>
          )}

          {transcriptUrl ? (
            <a
              href={transcriptUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#923D8C] hover:underline"
            >
              <ExternalLink size={16} />
              View Uploaded Transcript
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TranscriptAiReportModal;
